import { FC, useState } from 'react';

import { Checkbox, FormControlLabel, Link } from '@mui/material';

import { PrimaryButton, SecondaryButton } from '@/components/Shared/Buttons';
import { TrblPopup, TrblPopupActions, TrblPopupContent, TrblPopupTitle } from '@/components/Shared/Popup';
import { Text } from '@/components/Shared/Text';

import { useAcceptTermsAndConditions } from './hooks';

import { MESSAGES, URLS } from './constants';

import { canUserDismissPopup, isExistingUser } from './utils';

interface TermsAndConditionsPopupProps {
  onClose: () => void;
  onDismiss: () => void;
  isOpen: boolean;
  userCreatedAt?: string;
}

export const TermsAndConditionsPopup: FC<TermsAndConditionsPopupProps> = ({
  onClose,
  onDismiss,
  isOpen,
  userCreatedAt,
}) => {
  const [accepted, setAccepted] = useState(false);
  const { mutate: acceptTerms, isPending } = useAcceptTermsAndConditions();

  const handleAccept = () => {
    if (accepted) {
      acceptTerms();
      onClose();
    }
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAccepted(event.target.checked);
  };

  const canDismiss = canUserDismissPopup(userCreatedAt);
  const isExisting = isExistingUser(userCreatedAt);

  const getMessage = () => {
    return isExisting ? MESSAGES.EXISTING_USER : MESSAGES.NEW_USER;
  };

  return (
    <TrblPopup
      width="500px"
      maxwidth="90vw"
      hideBackdrop={false}
      aria-labelledby="Terms and Conditions"
      open={isOpen}
      disableEscapeKeyDown={!canDismiss}
      onClose={canDismiss ? onDismiss : undefined}>
      <TrblPopupTitle onClose={canDismiss ? onDismiss : undefined}>Terms and Conditions</TrblPopupTitle>
      <TrblPopupContent>
        <div style={{ marginBottom: '24px' }}>
          <Text type="regular-12px" style={{ marginBottom: '16px', lineHeight: '1.5', display: 'block' }}>
            {getMessage()}
          </Text>

          <FormControlLabel
            control={
              <Checkbox
                checked={accepted}
                onChange={handleCheckboxChange}
                style={{
                  color: '#39bd9d',
                }}
              />
            }
            label={
              <Text
                type="regular-12px"
                style={{
                  lineHeight: '32px',
                }}>
                I have read and agree to the{' '}
                <Link
                  href={URLS.TERMS_AND_CONDITIONS}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: '#39bd9d', textDecoration: 'underline' }}>
                  Terms and Conditions
                </Link>
              </Text>
            }
          />
        </div>
      </TrblPopupContent>
      <TrblPopupActions framed>
        <div>{canDismiss && <SecondaryButton label="Do this later" onClick={onDismiss} width={120} />}</div>
        <PrimaryButton label="Continue" disabled={!accepted || isPending} onClick={handleAccept} width={120} />
      </TrblPopupActions>
    </TrblPopup>
  );
};
